import React, { createContext, useContext, useState, useEffect } from "react";
import { getAppConfig } from "../services/api";

const defaultOrgLabels = {
  en: { singular: "Organization", plural: "Organizations" },
  es: { singular: "Organización", plural: "Organizaciones", gender: "feminine" },
};

const defaultCardLabels = {
  en: { singular: "Card", plural: "Cards" },
  es: { singular: "Tarjeta", plural: "Tarjetas" },
};

const ACCENT_COLORS = {
  indigo: "#4f46e5",
  blue: "#2563eb",
  violet: "#7c3aed",
  purple: "#9333ea",
  emerald: "#059669",
  teal: "#0d9488",
  cyan: "#0891b2",
  rose: "#e11d48",
  amber: "#d97706",
  sky: "#0284c7",
};

const defaultBranding = {
  icon: "/branding/icon.png",
  logo: "/branding/logo.png",
};

const AppConfigContext = createContext({
  appName: "App",
  maxProfiles: 1,
  maxUploadMb: 100,
  enableI18n: false,
  socialBaseUrls: {},
  branding: defaultBranding,
  cardLabels: defaultCardLabels,
  orgLabels: defaultOrgLabels,
  freeBlockedBlocks: [],
  tiers: [],
  blockTiers: {},
  featureTiers: {},
  paymentsEnabled: false,
  mpEnv: "test",
  mpCheckoutMode: "redirect",
  loginHeader: "text",
  loginLogo: null,
  accentColor: "#2563eb",
});

export function AppConfigProvider({ children }) {
  const [config, setConfig] = useState({
    appName: "App",
    maxProfiles: 1,
    maxUploadMb: 100,
    enableI18n: false,
    socialBaseUrls: {},
    branding: defaultBranding,
    cardLabels: defaultCardLabels,
    orgLabels: defaultOrgLabels,
    freeBlockedBlocks: [],
    tiers: [],
    blockTiers: {},
    featureTiers: {},
    paymentsEnabled: false,
    mpEnv: "test",
    mpCheckoutMode: "redirect",
    loginHeader: "text",
    loginLogo: null,
    accentColor: "#2563eb",
  });

  useEffect(() => {
    getAppConfig()
      .then((res) => {
        setConfig({
          appName: res.data.app_name,
          maxProfiles: res.data.max_profiles,
          maxUploadMb: res.data.max_upload_mb || 100,
          enableI18n: res.data.enable_i18n || false,
          socialBaseUrls: res.data.social_base_urls || {},
          branding: res.data.branding || defaultBranding,
          cardLabels: res.data.card_labels || defaultCardLabels,
          orgLabels: res.data.org_labels || defaultOrgLabels,
          freeBlockedBlocks: res.data.free_blocked_blocks || [],
          tiers: res.data.tiers || [],
          blockTiers: res.data.block_tiers || {},
          featureTiers: res.data.feature_tiers || {},
          paymentsEnabled: res.data.payments_enabled || false,
          mpEnv: res.data.mp_env || "test",
          mpCheckoutMode: res.data.mp_checkout_mode || "redirect",
          loginHeader: res.data.login_header || "text",
          loginLogo: res.data.login_logo || null,
          accentColor: ACCENT_COLORS[res.data.accent_color] || "#2563eb",
        });
        // Don't overwrite server-injected title on public profile pages
        if (!window.location.pathname.startsWith("/p/")) {
          document.title = res.data.app_name;
        }
      })
      .catch(() => {});
  }, []);

  return (
    <AppConfigContext.Provider value={config}>
      {children}
    </AppConfigContext.Provider>
  );
}

export const useAppConfig = () => useContext(AppConfigContext);
